import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Grid, IconButton, Collapse, Divider, useMediaQuery,
  TextField, Pagination, InputAdornment
} from '@mui/material';
import { Handshake, FileDownload, ExpandMore, Search } from '@mui/icons-material';
import { getDatabase, ref, get, query, orderByChild } from 'firebase/database';
import firebaseApp, { KEY_REF } from './adm/admin/firebaseConfig';
import { useTheme } from '@mui/material/styles';
import { getConfig } from './adm/ConfigContext';

function removePTags(html) {
  return html.replace(/<p>/g, '').replace(/<\/p>/g, '').replace(/<ol>.*?<\/ol>/gs, '').replace(/<li>.*?<\/li>/gs, '');
}

function truncateText(text, maxLength) {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

export default function Licitacoes() {
  const config = getConfig()
  const [licitacoes, setLicitacoes] = useState([]);
  const [filteredLicitacoes, setFilteredLicitacoes] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchLicitacoes = async () => {
      try {
        const databaseInstance = getDatabase(firebaseApp);
        const licitacoesRef = ref(databaseInstance, `licitacoes`);
        const snapshot = await get(licitacoesRef);
        const data = snapshot.val();

        if (data) {
          const licitacoesArray = Object.keys(data).map((key) => ({
            id: key,
            date: data[key].data,
            titulo: data[key].titulo,
            descricao: data[key].descricao,
            numero: data[key].numero,
            contrato: data[key].contrato || null,
            horario: data[key].horario || '',
            modalidade: data[key].modalidade,
            documentoUrl: data[key].documentoUrl,
            ativo: data[key].ativo,
          }));

          const licitacoesAtivas = licitacoesArray.filter((licitacao) => licitacao.ativo);
          const licitacoesInativas = licitacoesArray.filter((licitacao) => !licitacao.ativo);

          const licitacoesOrdenadas = [
            ...licitacoesAtivas.sort((a, b) => new Date(b.date) - new Date(a.date)),
            ...licitacoesInativas.sort((a, b) => new Date(b.date) - new Date(a.date)),
          ];


          setLicitacoes(licitacoesOrdenadas);
          setFilteredLicitacoes(licitacoesOrdenadas);
        }
      } catch (error) {
        console.error('Error fetching licitacoes:', error);
      }
    };

    fetchLicitacoes();
  }, []);

  const handleExpandClick = (id) => {
    setExpanded((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = licitacoes.filter((item) =>
      item.titulo.toLowerCase().includes(term) ||
      item.numero.toLowerCase().includes(term) ||
      item.descricao.toLowerCase().includes(term) ||
      item.modalidade.toLowerCase().includes(term)
    );

    setFilteredLicitacoes(filtered);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const renderListItem = (item) => (
    <Box
      key={item.id}
      itemscope
      itemtype="https://turmalina.tcepb.tc.br/documentation/Bidding"
      sx={{
        padding: 2,
        backgroundColor: item.ativo ? '#fff' : '#d3d3d3',
        borderRadius: 2,
        boxShadow: 1,
        marginBottom: 2,
        opacity: item.ativo ? 1 : 0.8,
      }}
    >
      <Grid container spacing={isMobile ? 1 : 2} direction={isMobile ? "column" : "row"} alignItems="center" justifyContent="space-between">
        <Grid item xs={isMobile ? 12 : 5}>
          <Box display="flex" alignItems="center">
            <IconButton color="primary" sx={{ marginRight: 1 }}>
              <Handshake sx={{ color: config?.color }} />
            </IconButton>
            <Typography variant={isMobile ? "body1" : "h7"} fontWeight="bold" sx={{ overflowWrap: 'break-word' }} itemprop="object">
              {truncateText(item.titulo, 80)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={isMobile ? 12 : 1}>
          <Typography variant="body2" color="textSecondary" textAlign={isMobile ? 'left' : 'center'} itemprop="bidID">
            {item.numero}
          </Typography>
        </Grid>
        <Grid item xs={isMobile ? 12 : 2}>
          <Typography variant="body2" color="textSecondary" textAlign={isMobile ? 'left' : 'center'} itemprop="publicationDate">
            {item.date}
          </Typography>
        </Grid>
        <Grid item xs={isMobile ? 12 : 3}>
          <Box display="flex" justifyContent={isMobile ? 'space-between' : 'center'}>
            <Button
              href={item.documentoUrl}
              target="_blank"
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              sx={{
                textTransform: 'uppercase',
                minWidth: '100px',
                fontSize: '0.875rem',
                backgroundColor: config?.color,
                '&:hover': {
                  backgroundColor: config?.colorSecondary,
                  color: config?.color
                },
                opacity: 1,
              }}
              itemprop="notice"
            >
              Edital
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => handleExpandClick(item.id)}
              endIcon={<ExpandMore />}
              sx={{
                textTransform: 'uppercase',
                minWidth: '110px',
                ml: 1,
                fontSize: '0.875rem',
                opacity: 1,
              }}
            >
              Detalhes
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Collapse in={expanded[item.id]} timeout="auto" unmountOnExit>
        <Box sx={{ padding: 2, mt: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
          {item.horario && <Typography variant="body2"><strong>Horário:</strong> {item.horario}</Typography>}
          <Typography variant="body2" itemprop="bidModality"><strong>Modalidade:</strong> {item.modalidade}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }} itemprop="object">
            <strong>Descrição:</strong> {removePTags(item.descricao)}
          </Typography>
          {item.contrato && <Button
            href={item.contrato}
            target="_blank"
            variant="contained"
            size="small"

            startIcon={<FileDownload />}
            sx={{
              mt: 1,
              textTransform: 'uppercase',
              minWidth: '120px',
              fontSize: '0.875rem',
              opacity: 1,
              backgroundColor: config?.color,
              '&:hover': {
                backgroundColor: config?.colorSecondary,
                color: config?.color
              },
            }}
            itemprop="notice"
          >
            Baixar Contrato
          </Button>}

          {item.aviso && <Button
            href={item.aviso}
            target="_blank"
            variant="contained"
            size="small"

            startIcon={<FileDownload />}
            sx={{
              mt: 1,
              ml: 1,
              textTransform: 'uppercase',
              minWidth: '120px',
              fontSize: '0.875rem',
              opacity: 1,
              backgroundColor: config?.color,
              '&:hover': {
                backgroundColor: config?.colorSecondary,
                color: config?.color
              },
            }}
            itemprop="notice"
          >
            Baixar Aviso
          </Button>}
        </Box>
      </Collapse>
      {!item.ativo && (
        <Typography variant="body2" sx={{ color: 'red', textAlign: 'center', fontWeight: 'bold', mt: 1 }}>
          Esta licitação já ocorreu e não está mais disponível, mas você pode ver como era o edital clicando em Baixar.
        </Typography>
      )}
      <Divider sx={{ marginTop: 2 }} />
    </Box>
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredLicitacoes.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ padding: 1, paddingLeft: { xs: 3, md: 15 }, paddingRight: { xs: 3, md: 15 } }}>
      <Box sx={{ py: 2, backgroundColor: '#f5f5f5', maxWidth: '1400px', mx: 'auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: 28, color: config?.color, fontWeight: 'bold' }}>
            LICITAÇÕES
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: isMobile ? '100%' : '300px' }}
          />
        </Box>
        <Divider sx={{ backgroundColor: config?.color, height: 2, mb: 2 }} />
        {currentItems.length > 0 ? (
          currentItems.map((item) => renderListItem(item))
        ) : (
          <Typography fontSize={19} align="center" sx={{ color: '#888', mt: 2 }}>
            Não há licitações disponíveis.
          </Typography>
        )}
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredLicitacoes.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Box>
    </Box>
  );
}
